import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { categoryLabel, resolveImage } from "@/lib/api";
import ShareCard from "@/components/ShareCard";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";

export default function PostReader() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    setPost(null);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError("This piece could not be found."));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="post-error">
        <p className="font-serif-display text-3xl text-ink-soft italic">{error}</p>
        <Link to="/writings" className="mt-8 inline-block text-sm uppercase tracking-[0.18em] text-terracotta link-underline">
          Back to writings
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32" data-testid="post-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <article className="bg-paper" data-testid="post-page">
      <Seo
        title={post.title}
        description={post.excerpt || `A ${categoryLabel(post.category)} by Barbara Ruffing.`}
        image={resolveImage(post.cover_image)}
        type="article"
      />
      {post.cover_image && (
        <div className="w-full bg-paper-2/40 border-b border-rule flex items-center justify-center overflow-hidden">
          <img
            src={resolveImage(post.cover_image)}
            alt={post.title}
            data-testid="post-cover"
            className="block w-auto max-w-full max-h-[60vh] object-contain"
          />
        </div>
      )}

      <header className="max-w-2xl mx-auto px-6 pt-16 lg:pt-24">
        <Link
          to="/writings"
          data-testid="post-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-10"
        >
          <ArrowLeft size={14} /> All Writings
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          {categoryLabel(post.category)}
        </p>
        <h1
          data-testid="post-title"
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]"
        >
          {post.title}
        </h1>
        {post.published_at && (
          <p className="mt-6 text-sm text-ink-soft uppercase tracking-[0.18em]">
            {new Date(post.published_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}{" "}
            · Barbara Ruffing
          </p>
        )}
        <div className="mt-8">
          <ShareButton
            title={post.title}
            excerpt={post.excerpt}
            url={typeof window !== "undefined" ? `${window.location.origin}/api/share/post/${post.slug}` : undefined}
          />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12 lg:py-16">
        {post.excerpt && (
          <p className="font-serif-display text-2xl italic text-ink-soft border-l-2 border-terracotta pl-6 mb-10 leading-snug">
            {post.excerpt}
          </p>
        )}
        {/^\s*</.test(post.content || "") ? (
          <div
            className="prose-editorial"
            data-testid="post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : (
          <div
            className="prose-editorial whitespace-pre-wrap drop-cap"
            data-testid="post-content"
          >
            {post.content}
          </div>
        )}

        <div className="mt-20 border-t border-rule pt-10 text-center">
          <p className="font-serif-display italic text-xl text-ink-soft">
            Thank you for reading.
          </p>
          <Link
            to="/writings"
            className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-terracotta link-underline"
            data-testid="post-end-back"
          >
            Read more writings
          </Link>

          <div className="mt-10 flex justify-center">
            <ShareButton
              title={post.title}
              excerpt={post.excerpt}
              testId="share-piece-btn-bottom"
              url={typeof window !== "undefined" ? `${window.location.origin}/api/share/post/${post.slug}` : undefined}
            />
          </div>

          <div className="mt-10">
            <ShareCard
              title={post.title}
              category={post.category}
              authorName="Barbara Ruffing"
            />
          </div>
        </div>
      </div>
    </article>
  );
}
